import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { RegisterPayload } from "../../../api/types/auth.types";
import AuthService from "../../../api/services/auth.service";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import Input from "../../../components/common/input/Input";
import Button from "../../../components/common/button/Button";
import Select from "../../../components/common/select/Select";
import styles from "./Register.module.scss";
import LocalizedLink from "../../../components/common/localized-link/LocalizedLink";
import { useLocalizedNavigate } from "../../../hooks/useLocalizedNavigate";
import { useTranslation } from "react-i18next";
import { initiateGoogleOAuth } from "../../../utils/googleOAuth";

type PasswordRules = {
  length: boolean;
  upper: boolean;
  lower: boolean;
  number: boolean;
  symbol: boolean;
};

const Register = () => {
  const { t, i18n } = useTranslation("translation");
  const { setEmail } = useAuth();
  const [formValues, setFormValues] = useState<Partial<RegisterPayload>>({});
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useLocalizedNavigate();
  const [errors, setErrors] = useState<
    Partial<Record<keyof RegisterPayload, string>>
  >({});
  const [loading, setLoading] = useState(false);

  const [passwordRules, setPasswordRules] = useState<PasswordRules>({
    length: false,
    upper: false,
    lower: false,
    number: false,
    symbol: false,
  });

  const getTranslatedLanguages = (): Array<{ id: string; name: string }> => {
    const currentLang = i18n.language;

    const languageTranslations: Record<string, Record<string, string>> = {
      az: {
        az: "Azərbaycan",
        en: "İngilis",
        de: "Alman",
        es: "İspan",
      },
      en: {
        az: "Azerbaijani",
        en: "English",
        de: "German",
        es: "Spanish",
      },
      de: {
        az: "Aserbaidschanisch",
        en: "Englisch",
        de: "Deutsch",
        es: "Spanisch",
      },
      es: {
        az: "Azerbaiyano",
        en: "Inglés",
        de: "Alemán",
        es: "Español",
      },
    };

    const translations =
      languageTranslations[currentLang] || languageTranslations.en;

    return [
      { id: "az", name: translations.az },
      { id: "en", name: translations.en },
      { id: "de", name: translations.de },
      { id: "es", name: translations.es },
    ];
  };

  const getSelectPlaceholder = () => {
    const currentLang = i18n.language;
    const placeholders: Record<string, string> = {
      az: "Seçin...",
      en: "Select...",
      de: "Auswählen...",
      es: "Seleccionar...",
    };
    return placeholders[currentLang] || "Select...";
  };

  useEffect(() => {
    if (formValues.password) {
      const password = formValues.password;
      setPasswordRules({
        length: password.length >= 8,
        upper: /[A-Z]/.test(password),
        lower: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        symbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      });
    } else {
      setPasswordRules({
        length: false,
        upper: false,
        lower: false,
        number: false,
        symbol: false,
      });
    }
  }, [formValues.password]);

  const handleChange = (field: keyof RegisterPayload, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));

    if (field === "password") {
      setErrors((prev) => ({ ...prev, password: "" }));
    }
  };

  const validateForm = () => {
    let newErrors: Partial<Record<keyof RegisterPayload, string>> = {};

    if (!formValues?.first_name?.trim()) {
      newErrors.first_name = t("errors.firstNameRequired") || "";
    }
    if (!formValues?.last_name?.trim()) {
      newErrors.last_name = t("errors.lastNameRequired") || "";
    }
    if (!formValues?.email?.trim()) {
      newErrors.email = t("errors.emailRequired") || "";
    }
    if (!formValues?.preferred_language?.trim()) {
      newErrors.preferred_language =
        t("errors.preferredLanguageRequired") || "";
    }
    if (!formValues.confirm_password) {
      newErrors.confirm_password = t("errors.confirmPasswordRequired") || "";
    }
    if (formValues.confirm_password !== formValues.password) {
      newErrors.confirm_password = t("errors.passwordsNotMatch") || "";
    }
    if (!formValues?.phone?.trim()) {
      newErrors.phone = t("errors.phoneRequired") || "";
    }

    if (!formValues.password) {
      newErrors.password = t("errors.passwordRequired") || "";
    } else {
      const allRulesMet = Object.values(passwordRules).every((rule) => rule);
      if (!allRulesMet) {
        const failedRules: string[] = [];

        if (!passwordRules.length) {
          failedRules.push(
            t("psw-rules.errorMessages.length") || t("psw-rules.psw-length"),
          );
        }
        if (!passwordRules.upper) {
          failedRules.push(
            t("psw-rules.errorMessages.upper") || t("psw-rules.psw-upper"),
          );
        }
        if (!passwordRules.lower) {
          failedRules.push(
            t("psw-rules.errorMessages.lower") || t("psw-rules.psw-lower"),
          );
        }
        if (!passwordRules.number) {
          failedRules.push(
            t("psw-rules.errorMessages.number") || t("psw-rules.psw-number"),
          );
        }
        if (!passwordRules.symbol) {
          failedRules.push(
            t("psw-rules.errorMessages.symbol") || t("psw-rules.psw-symbol"),
          );
        }

        if (failedRules.length > 0) {
          newErrors.password = failedRules.join(", ");
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", formValues.email || "");
      formData.append("password", formValues.password || "");
      formData.append("confirm_password", formValues.confirm_password || "");
      formData.append("contact_phone", formValues.phone || "");
      formData.append("first_name", formValues.first_name || "");
      formData.append("last_name", formValues.last_name || "");
      formData.append(
        "preferred_language",
        formValues.preferred_language || "",
      );
      const response = await AuthService.register(formData);
      setEmail(formValues.email || "");
      // With OTP confirmation disabled server-side the account comes back
      // already active, so there is no code to ask for.
      if (response.data?.otp_required === false) {
        navigate("/auth/login");
      } else {
        navigate("/auth/otp");
      }
    } catch (error: any) {
      setLoading(false);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("common_error") ||
        "An error occurred";
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  const getRuleClassName = (isMet: boolean) => {
    return isMet ? styles.ruleMet : styles.ruleNotMet;
  };

  const getTranslation = (key: string, defaultValue: string = ""): string => {
    const result = t(key);
    return result || defaultValue;
  };

  return (
    <div className={styles.register}>
      <TitleUpdater title={getTranslation("sign_up", "Sign Up")} />
      <div className="container">
        <div className={styles.content}>
          <form action="" className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.head}>
              <h1 className={styles.title}>
                {getTranslation("sign_up", "Sign Up")}
              </h1>
              <p className={styles.description}>
                {getTranslation("register_subtitle", "Create your account")}
              </p>
              {errorMessage && (
                <p className={styles.errorMessage}>{errorMessage}</p>
              )}
            </div>
            <div className={styles.inputs}>
              <Input
                label={getTranslation("firstName", "First Name")}
                placeholder={getTranslation(
                  "placeholders.firstName",
                  "Enter your first name",
                )}
                type="text"
                id="first_name"
                value={formValues.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
                className={styles.input}
                error={errors.first_name}
              />
              <Input
                label={getTranslation("lastName", "Last Name")}
                placeholder={getTranslation(
                  "placeholders.lastName",
                  "Enter your last name",
                )}
                type="text"
                id="last_name"
                value={formValues.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
                className={styles.input}
                error={errors.last_name}
              />
              <Input
                label={getTranslation("email", "Email")}
                placeholder={getTranslation(
                  "placeholders.email",
                  "Enter your email",
                )}
                type="email"
                id="email"
                value={formValues.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className={styles.input}
                error={errors.email}
              />
              <Input
                label={getTranslation("phone", "Phone")}
                placeholder={getTranslation(
                  "placeholders.phone",
                  "Enter your phone number",
                )}
                type="phone"
                id="phone"
                value={formValues.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className={styles.input}
                error={errors.phone}
                phone
              />
              <Select
                label={getTranslation(
                  "preferred_language",
                  "Preferred Language",
                )}
                value={formValues.preferred_language || ""}
                options={getTranslatedLanguages()}
                placeholder={getSelectPlaceholder()}
                onChange={(value) =>
                  handleChange("preferred_language", value.id)
                }
                className={`${styles.input} ${styles.fullWidth}`}
                error={errors.preferred_language}
              />
              <Input
                label={getTranslation("password", "Password")}
                placeholder="••••••••••"
                type="password"
                id="password"
                value={formValues.password}
                onChange={(e) => handleChange("password", e.target.value)}
                className={styles.input}
                error={errors.password}
              />
              <Input
                label={getTranslation("confirm_password", "Confirm Password")}
                placeholder="••••••••••"
                type="password"
                id="confirm_password"
                value={formValues.confirm_password}
                onChange={(e) =>
                  handleChange("confirm_password", e.target.value)
                }
                className={styles.input}
                error={errors.confirm_password}
              />
              <ul className={styles.passwordRules} id="psw-rules">
                <li className={getRuleClassName(passwordRules.length)}>
                  {getTranslation(
                    "psw-rules.psw-length",
                    "Must be at least 8 characters",
                  )}
                </li>
                <li className={getRuleClassName(passwordRules.upper)}>
                  {getTranslation(
                    "psw-rules.psw-upper",
                    "Must contain an uppercase letter (A–Z)",
                  )}
                </li>
                <li className={getRuleClassName(passwordRules.lower)}>
                  {getTranslation(
                    "psw-rules.psw-lower",
                    "Must contain a lowercase letter (a–z)",
                  )}
                </li>
                <li className={getRuleClassName(passwordRules.number)}>
                  {getTranslation(
                    "psw-rules.psw-number",
                    "Must contain a number (0–9)",
                  )}
                </li>
                <li className={getRuleClassName(passwordRules.symbol)}>
                  {getTranslation(
                    "psw-rules.psw-symbol",
                    "Must contain a special character (!@#$...)",
                  )}
                </li>
              </ul>
            </div>
            <div className={styles.login_btn}>
              <Button loading={loading} disabled={loading} type="submit">
                {getTranslation("sign_up", "Sign Up")}
              </Button>
            </div>
            <div className={styles.divider}>
              <div className={styles.dividerLine}></div>
              <span className={styles.dividerText}>
                {getTranslation("or", "OR")}
              </span>
              <div className={styles.dividerLine}></div>
            </div>
            <div className={styles.google_btn}>
              <Button
                type="button"
                onClick={initiateGoogleOAuth}
                disabled={loading}
                aria-label="Continue with Google"
                className={styles.googleButton}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {getTranslation("continue_with_google", "Continue with Google")}
              </Button>
            </div>
            <div className={styles.footer}>
              <p>
                {getTranslation("already_user", "Already have an account?")}
              </p>
              <LocalizedLink to="/auth/login">
                {getTranslation("login", "Login")}
              </LocalizedLink>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
